// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Curve from '../../../addons/particlepack/curves/Curve';

/**
 * A collection of Curve instances. Used to connect different types curves, joining them at their given time offsets.
 */
class PolyCurve extends Curve {
  /**
   * @param {object} [options]
   * @param {Array<Curve>} [options.segments]
   */
  constructor(options) {
    options = options || {};

    super(options);

    /**
     * @type {Array<Curve>}
     */
    this.segments = options.segments ? options.segments.map(curve => curve.clone()) : [];
  }

  clone() {
    return new PolyCurve(this);
  }

  /**
   * @param {Curve} curve
   */
  addSegment(curve) {
    this.segments.push(curve);
    this.sort();
  }

  /**
   * @param {number} i
   */
  removeSegment(i) {
    this.segments.splice(i, 1);
  }

  /**
   * Sorts the segments depending on their timeOffset.
   */
  sort() {
    this.segments = this.segments.sort((curveA, curveB) => curveA.timeOffset - curveB.timeOffset);
  }

  /**
   * Returns a GLSL expression that gives the value of the curve at a given time.
   * @param {string} timeVariableName
   * @param {string} lerpValueVariableName
   */
  toGLSL(timeVariableName, lerpValueVariableName) {
    const segments = this.segments;
    const glsl = [];
    for (let i = 0; i < segments.length; i++) {
      const a = segments[i];
      const t0 = Curve.numberToGLSL(a.timeOffset);
      let t1 = '1.0';
      if (i < segments.length - 1) {
        t1 = Curve.numberToGLSL(segments[i + 1].timeOffset);
      }
      glsl.push(
        `step(${t0},t)*step(-${t1},-t)*${a.toGLSL(timeVariableName, lerpValueVariableName)}`,
      );
    }
    return glsl.join('+');
  }

  /**
   * Returns a GLSL expression that gives the integral value of the curve at a given time.
   * @param {string} timeVariableName
   * @param {string} lerpValueVariableName
   */
  integralToGLSL(timeVariableName, lerpValueVariableName) {
    const segments = this.segments;
    const glsl = [];
    for (let i = 0; i < segments.length; i++) {
      const a = segments[i];
      const t0 = Curve.numberToGLSL(a.timeOffset);
      let t1 = '1.0';
      if (i < segments.length - 1) {
        t1 = Curve.numberToGLSL(segments[i + 1].timeOffset);
      }
      glsl.push(
        a.integralToGLSL(
          `clamp(${timeVariableName},${t0},${t1})`,
          lerpValueVariableName,
        ),
      );
    }
    return glsl.join('+');
  }

  /**
   * Get the value of the curve at a given time.
   * @param {number} t
   * @param {number} lerpValue
   * @returns {number}
   */
  getValueAt(t, lerpValue) {
    // Find the matching segment
    const segments = this.segments;
    for (let i = 0; i < segments.length - 1; i++) {
      const a = segments[i];
      const b = segments[i + 1];
      if (a.timeOffset <= t && b.timeOffset > t) {
        return this.segments[i].getValueAt(t, lerpValue);
      }
    }
    return this.segments[segments.length - 1].getValueAt(t, lerpValue);
  }

  /**
   * Get the integral value of the curve at a given time.
   * @param {number} t
   * @param {number} lerpValue
   * @returns {number}
   */
  getIntegralValueAt(t, lerpValue) {
    // Add all integral values until the last segment
    const segments = this.segments;
    let value = 0;
    for (let i = 0; i < segments.length; i++) {
      const a = segments[i];
      let t1 = 1;
      if (i < segments.length - 1) {
        t1 = segments[i + 1].timeOffset;
      }
      if (a.timeOffset <= t && t1 > t) {
        value += this.segments[i].getIntegralValueAt(t, lerpValue);
        break;
      } else {
        value += this.segments[i].getIntegralValueAt(t1, lerpValue);
      }
    }
    return value;
  }
}

export default PolyCurve;
